import { type FunctionComponent } from "react";
import { ImageSlider } from "@App/Components/ImageSlider/ImageSlider";
import { useSelector } from "react-redux";
import { State } from "@App/state";
import { useQuery } from "@apollo/client";
import { GarageDetailsQuery, GET_GARAGE_DETAILS_QUERY } from "@App/graphql/queries";
import { ProfileDetailsSkeleton } from "@App/Components/Skeletons/ProfileDetailsSkeleton";
import { useTranslation } from "react-i18next";
import { OrganizationIsRepareo } from "@App/utils/conditionals/organization";

export const ProfileDetails: FunctionComponent = () => {
  const { t } = useTranslation();
  const garage = useSelector((state: State) => state.orders.garage);

  const { loading, data, error } = useQuery<GarageDetailsQuery>(GET_GARAGE_DETAILS_QUERY, {
    variables: {
      id: garage && (typeof garage.id === "string" ? parseInt(garage.id.toString()) : garage.id),
    },
    skip: !garage || (garage && !!garage.opening_hours && garage.opening_hours.length > 0), // skip if details already loaded
  });

  if (loading) {
    return <ProfileDetailsSkeleton />;
  }

  if (error) {
    return <div></div>;
  }

  const garageDetails = data ? data.garage : garage;

  const formatDescription = (text) => {
    return text
      .replace(/<(?!\/?(?:p|ul|ol|li)\b)[^>]*>/g, "")
      .replace(/<(p|ul|ol|li)[^>]*>/g, "<$1>");
  };

  return (
    <section className="container mx-auto my-10">
      <div className={"text-2xl font-serif"}>
        {t("profileDetails.headline", {
          garageType: garage?.garageType?.display_name_singular,
        })}
      </div>

      <div className="my-10">
        <ImageSlider images={garageDetails.images} />
      </div>

      <div>
        <div className="font-bold mt-5 mb-3">{t("profileDetails.opening-hours")}</div>
        <div className={"flex"}>
          <div className={"flex flex-col mr-8"}>
            <span>{t("profileDetails.days.monday")}</span>
            <span>{t("profileDetails.days.tuesday")}</span>
            <span>{t("profileDetails.days.wednesday")}</span>
            <span>{t("profileDetails.days.thursday")}</span>
            <span>{t("profileDetails.days.friday")}</span>
            <span>{t("profileDetails.days.saturday")}</span>
            <span>{t("profileDetails.days.sunday")}</span>
          </div>
          <div className={"flex flex-col"}>
            <span>
              {garageDetails.opening_hours.find((openingHours) => openingHours.day === 1).open}
            </span>
            <span>
              {garageDetails.opening_hours.find((openingHours) => openingHours.day === 2).open}
            </span>
            <span>
              {garageDetails.opening_hours.find((openingHours) => openingHours.day === 3).open}
            </span>
            <span>
              {garageDetails.opening_hours.find((openingHours) => openingHours.day === 4).open}
            </span>
            <span>
              {garageDetails.opening_hours.find((openingHours) => openingHours.day === 5).open}
            </span>
            <span>
              {garageDetails.opening_hours.find((openingHours) => openingHours.day === 6).open}
            </span>
            <span>
              {garageDetails.opening_hours.find((openingHours) => openingHours.day === 7).open}
            </span>
          </div>
        </div>
      </div>

      {organization && OrganizationIsRepareo(organization) && (
        <div className={"w-full my-10"}>
          <p className="font-bold mt-5 mb-3">{t("profileDetails.description")}</p>
          <p>
            {garageDetails.description && (
              <div
                className={"garage-description"}
                dangerouslySetInnerHTML={{
                  __html: formatDescription(garageDetails.description),
                }}
              />
            )}
            {!garageDetails.description &&
              t("profileDetails.default-description", {
                garage: garageDetails.name,
                garageType: garage?.garageType?.display_name_singular,
              })}
          </p>
        </div>
      )}
    </section>
  );
};
